import { Box, makeStyles } from "@material-ui/core";
import { InfoOutlined } from "@material-ui/icons";
import * as React from "react";
import * as $ from "jquery";
import { Colors } from "styles/Colors";
import Leaderboard from "common/components/Leaderboard/Leaderboard";
import UserManager from "common/userManager";
import TrophyService from "services/TrophyService";
import SeasonService from "services/SeasonService";
import UserCard from "common/components/UserCard";
import ContentHeader from "common/components/ContentHeader/ContentHeader";
import HtmlTooltip from "common/components/HtmlTooltip/HtmlTooltip";
import { toMonthNameDayAndYear } from "common/helpers/dateHelper";

const useStyles = makeStyles({
	root: {
		backgroundColor: Colors.panel,
		borderRadius: 12,
		padding: 10,
		marginBottom: 12,
	},
	headerRow: {
		display: "flex",
		alignItems: "center",
		marginBottom: 5,
		"& h4": {
			marginBottom: 0,
		},
	},
	infoIcon: {
		color: Colors.pickstop,
		fontSize: 18,
		marginLeft: 6,
		cursor: "help",
		display: "block",
		position: "relative",
		top: -2,
	},
	heading: {
		color: Colors.pickstop,
		marginTop: 5,
		marginBottom: 5,
		marginleft: 10,
	},
	leaderboardDetails: {
		textAlign: "center",
		margin: "0 10px",
		position: "relative",
	},
	fullSvg: {
		display: "inline-block",
		position: "relative",
		left: -5,
		top: 3,
		stroke: Colors.primary,
		fill: Colors.primary,
	},
	yourSvg: {
		display: "inline-block",
		position: "relative",
		left: -3,
		top: 1,
		stroke: Colors.primary,
		fill: Colors.primary,
	},
});

// Render a user's top trophies.
const trophyImages = (record) => (
	<span className="topTrophies">
		{[1, 2, 3, 4, 5].map((n) => {
			const src = record[`trophy${n}ImagePath`];
			if (!src) {
				return null;
			}
			const name = record[`event${n}Name`];
			return (
				<img key={n} className="awardImage" alt={name || "Trophy"} src={src} />
			);
		})}
	</span>
);

export default function AwardLeaders(props) {
	const styles = useStyles();

	const [awardLeaderState, setAwardLeaderState] = React.useState({
		items: [],
		totalRecords: 0,
		rowNumberToGoTo: 1,
		userIdToGoTo: null,
	});
	const [currentUserRow, setCurrentUserRow] = React.useState(null);
	const [season, setSeason] = React.useState(null);

	React.useEffect(() => {
		TrophyService.getTrophyLeaderboard().then((leaderboardData) => {
			let copy = { ...awardLeaderState };
			copy.items = leaderboardData;
			setAwardLeaderState(copy);
		});
		TrophyService.getTrophyLeaderboardUserRow(UserManager.getUserId()).then(
			(data) => {
				setCurrentUserRow(data);
			}
		);
		SeasonService.getCurrent()
			.then((data) => setSeason(data))
			.catch(() => setSeason(null));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getLeaderboardData = (startRow, overwriteData, isUp) => {
		const def = $.Deferred();
		TrophyService.getTrophyLeaderboard(startRow).then((leaderboard) => {
			let items = [...awardLeaderState.items];
			let newItems = null;
			if (overwriteData) {
				newItems = leaderboard;
			} else {
				if (isUp) {
					newItems = leaderboard.concat(items);
				} else {
					newItems = items.concat(leaderboard);
				}
			}
			let copy = { ...awardLeaderState };
			copy.items = newItems;
			copy.rowNumberToGoTo = 1;
			copy.userIdToGoTo = null;
			setAwardLeaderState(copy);
			def.resolve(newItems.length > 0);
		});
		return def.promise();
	};

	const getLeaderboardAdditionalData = (fromTop) => {
		return TrophyService.getTrophyLeaderboardAdditional(fromTop).then(
			(info) => {
				let itemIdx = getItemIdx(UserManager.getUserId(), info.records);
				let copy = { ...awardLeaderState };
				copy.items = info.records;
				copy.totalRecords = info.totalRecords;
				copy.rowNumberToGoTo = fromTop || !itemIdx ? 1 : itemIdx;
				copy.userIdToGoTo = fromTop ? null : UserManager.getUserId();
				setAwardLeaderState(copy);
			}
		);
	};

	const searchLeaderboardData = (term) => {
		return TrophyService.searchTrophyLeaderboard(term).then((data) => {
			let copy = { ...awardLeaderState };
			copy.items = data;
			copy.totalRecords = data.length;
			copy.rowNumberToGoTo = 1;
			copy.userIdToGoTo = null;
			setAwardLeaderState(copy);
		});
	};

	const getItemIdx = (id, items) => {
		if (!id || !items) {
			return null;
		}
		return items.findIndex((r) => {
			return r.userId === id;
		});
	};

	const leaderboardRowJsx = (record) => {
		return (
			<>
				<span className="position">{record.rank}</span>
				<span className="name">
					<UserCard
						userId={record.userId}
						dontLoadData={true}
						userName={record.username}
						pinnedEventName={record.pinnedEventName}
						pinnedTrophyImagePath={record.pinnedTrophyImagePath}
						nameplateId={record.nameplateId}
					/>
				</span>
				{trophyImages(record)}
			</>
		);
	};

	const meId = UserManager.getUserId();
	const meInTop10 = awardLeaderState.items
		.slice(0, 10)
		.some((r) => r.userId === meId);
	const currentUserRowJsx =
		!!currentUserRow && !meInTop10 ? leaderboardRowJsx(currentUserRow) : null;
	const seasonTooltip = season
		? `Ranked by trophies earned this season. Current season: ${toMonthNameDayAndYear(
				season.startDate
		  )} – ${toMonthNameDayAndYear(season.endDate)}.`
		: "Ranked by trophies earned this season.";
	const headerJsx = (
		<div className={styles.headerRow}>
			<ContentHeader text="Award Leaders" />
			<HtmlTooltip title={seasonTooltip}>
				<InfoOutlined className={styles.infoIcon} />
			</HtmlTooltip>
		</div>
	);
	const tableDataAry = !awardLeaderState.items
		? []
		: awardLeaderState.items.map((record) => {
				return leaderboardRowJsx(record);
		  });

	const modalDataAry = awardLeaderState.items.map((record, index) => {
		return (
			<div data-id={record.userId} key={index} className={"leaderboard-row"}>
				<span className="position">{record.rank}</span>

				<span className="name">
					<UserCard
						userId={record.userId}
						dontLoadData={true}
						userName={record.username}
						pinnedEventName={record.pinnedEventName}
						pinnedTrophyImagePath={record.pinnedTrophyImagePath}
						nameplateId={record.nameplateId}
					/>
				</span>
				{trophyImages(record)}
			</div>
		);
	});

	if (!awardLeaderState.items || awardLeaderState.items.length < 1) {
		return (
			<Box className={styles.root}>
				{headerJsx}
				No leaderboard data to show.
			</Box>
		);
	}
	const hasItems =
		!!awardLeaderState &&
		awardLeaderState.items &&
		awardLeaderState.items.length > 0;
	return (
		<>
			<Box className={styles.root}>
				{headerJsx}
				<Leaderboard
					leaderboardClass={`trophy`}
					loadData={getLeaderboardData}
					loadDataAddtl={getLeaderboardAdditionalData}
					totalRecords={awardLeaderState.totalRecords}
					leaderboardHeaders={[``, `Name`, `Top Trophies`]}
					tableData={tableDataAry}
					modalTableData={modalDataAry}
					eventData={null}
					hasBracket={true}
					noDataEl={null}
					firstRow={hasItems ? awardLeaderState.items[0].rank : 0}
					lastRow={
						hasItems
							? awardLeaderState.items[awardLeaderState.items.length - 1].rank
							: 0
					}
					rowNumberToGoTo={awardLeaderState.rowNumberToGoTo}
					userIdToGoTo={awardLeaderState.userIdToGoTo}
					searchLeaderboard={searchLeaderboardData}
					totalEntrants={hasItems ? awardLeaderState.items[0].totalRecords : 0}
					currentUserRow={currentUserRowJsx}
				/>
			</Box>
		</>
	);
}
